import React, { useCallback, useContext, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { UI } from "@wwimmo/ui";
import { RootStoreContext } from "src/stores/RootStore";
import { useHistory, useParams } from "react-router-dom";
import { observer } from "mobx-react";
import { Step } from "src/screens/process/step/Step";
import { useForm, SubmitHandler } from "react-hook-form";
import { runInAction, toJS } from "mobx";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import { IMAGE_TYPE, Key } from "src/api/Notification";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "src/screens/process/keys/Keys.css";
import { ImageSelector } from "src/components/imageSelector";

const KeysBase = () => {
    const { t } = useTranslation();
    const { easyContactStore, uiStore } = useContext(RootStoreContext);
    const history = useHistory();
    const { processId } = useParams<{ processId: string; stepId: string }>();
    const { nextStep } = easyContactStore.stepNavigation;

    const key_schema = yup.object().shape({
        amount: yup
            .number()
            .typeError(t("form.number_field"))
            .required(t("form.required_field"))
            .positive(t("form.number_field"))
            .integer(t("form.number_field")),
        lost: yup.boolean().required(t("form.required_field")),
        inscription: yup.string().required(t("form.required_field"))
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        setFocus
    } = useForm<Key>({
        resolver: yupResolver(key_schema)
    });

    useEffect(() => {
        if (easyContactStore.notification.key?.keyimage1) {
            setValue("keyimage1", toJS(easyContactStore.notification.key?.keyimage1));
        }
        if (easyContactStore.notification.key?.keyimage2) {
            setValue("keyimage2", toJS(easyContactStore.notification.key?.keyimage2));
        }
    }, [setValue, easyContactStore.notification.key?.keyimage1, easyContactStore.notification.key?.keyimage2]);

    useEffect(() => {
        setValue("amount", easyContactStore.notification.key?.amount || 1);
        setValue("lost", easyContactStore.notification.key?.lost || false);
        setValue("inscription", easyContactStore.notification.key?.inscription || "");
        setFocus("amount");
    }, [easyContactStore.notification.key, setValue, setFocus]);

    const onSubmit: SubmitHandler<Key> = useCallback(
        (data: Key) => {
            runInAction(() => {
                if (data.inscription) {
                    easyContactStore.setKey({ ...data });
                }
            });
            if (nextStep) {
                history.push(
                    selectRoute(Route.step, { processId, stepType: String(nextStep.type), stepId: nextStep.id })
                );
            }
        },
        [history, easyContactStore, nextStep, processId]
    );

    const onChangeLost: React.ChangeEventHandler<HTMLInputElement> = useCallback(
        (event) => {
            runInAction(() => {
                if (event.target) {
                    setValue("lost", event.target.checked);
                }
            });
        },
        [setValue]
    );

    return (
        <Step>
            <UI.Container className="pt-4">
                <UI.Row>
                    <UI.Col md={uiStore.defaultColumnSize.md}>
                        <UI.Form onSubmit={handleSubmit(onSubmit)}>
                            <UI.Input
                                {...register("amount")}
                                label={`${t("screens.keys.amount")} *`}
                                type="number"
                                errorMsg={errors.amount ? errors.amount.message : undefined}
                            />
                            <UI.Checkbox
                                {...register("lost")}
                                label={t("screens.keys.lost")}
                                onChange={onChangeLost}
                                className="Checkbox"
                            />
                            <UI.Input
                                {...register("inscription")}
                                label={`${t("screens.keys.inscription")} *`}
                                type="text"
                                as="textarea"
                                errorMsg={errors.inscription ? errors.inscription.message : undefined}
                            />
                            <ImageSelector.Container>
                                <ImageSelector.Item
                                    label={`${t("screens.keys.keyimage")} 1`}
                                    name="keyimage1"
                                    imagetype={IMAGE_TYPE.KEY}
                                    setValue={setValue}
                                    initialImageUrl={easyContactStore.notification.damage?.image2?.url}
                                />
                                <ImageSelector.Item
                                    label={`${t("screens.keys.keyimage")} 2`}
                                    name="keyimage2"
                                    imagetype={IMAGE_TYPE.KEY}
                                    setValue={setValue}
                                    initialImageUrl={easyContactStore.notification.damage?.image2?.url}
                                />
                            </ImageSelector.Container>
                            <UI.Button onClick={handleSubmit(onSubmit)} label={t("labels.continue")} />
                        </UI.Form>
                    </UI.Col>
                </UI.Row>
            </UI.Container>
        </Step>
    );
};

export const Keys = Sentry.withProfiler(observer(KeysBase));
